import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSite } from "./App";
import { resolveImageUrl } from "@/lib/api";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

const CAT_LABELS = {
  all:         "Wszystkie",
  realizacje:  "Realizacje",
  podmurowki:  "Podmurówki",
  siatki:      "Siatki",
  slupki:      "Słupki",
  panele:      "Panele",
  inne:        "Inne",
};

const Gallery = () => {
  const { gallery } = useSite();
  const [activeCat, setActiveCat] = useState("all");
  const [openIdx, setOpenIdx] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Build only the categories that actually have items, in stable order
  const categories = useMemo(() => {
    const present = new Set((gallery || []).map((g) => g.category));
    const ordered = ["all", "realizacje", "podmurowki", "siatki", "slupki", "panele", "inne"];
    return ordered.filter((c) => c === "all" || present.has(c));
  }, [gallery]);

  const filtered = useMemo(() => {
    if (!gallery) return [];
    if (activeCat === "all") return gallery;
    return gallery.filter((g) => g.category === activeCat);
  }, [gallery, activeCat]);

  const close = useCallback(() => setOpenIdx(null), []);
  const next = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i + 1) % filtered.length)),
    [filtered.length]
  );
  const prev = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length)),
    [filtered.length]
  );

  // Keyboard navigation in lightbox
  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIdx, close, next, prev]);

  // When category changes while lightbox open — close to avoid stale index
  useEffect(() => { setOpenIdx(null); setExpanded(false); }, [activeCat]);

  if (!gallery || gallery.length === 0) return null;

  const current = openIdx !== null ? filtered[openIdx] : null;
  const INITIAL_VISIBLE = 6;
  const visible = expanded ? filtered : filtered.slice(0, INITIAL_VISIBLE);
  const hiddenCount = Math.max(0, filtered.length - INITIAL_VISIBLE);

  return (
    <section
      id="galeria"
      className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-[#0a0a0b]"
      data-testid="gallery-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-6 sm:mb-8">
          <span className="font-mono text-xs text-[#a8a29e]">[ 03 ]</span>
          <div className="h-px w-16 bg-[#ea580c]" />
          <span className="font-mono text-xs text-[#ea580c] uppercase tracking-[0.2em]">Galeria</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-end mb-8 sm:mb-12">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-8xl uppercase leading-[0.95] sm:leading-[0.9]">
            Zobacz nasze <span className="text-[#ea580c]">produkty</span>
            <br/>i&nbsp;przykładowe realizacje.
          </h2>
          <p className="text-base sm:text-lg text-[#a8a29e] max-w-xl">
            Kliknij dowolne zdjęcie, by je powiększyć. Filtry pomogą Ci znaleźć interesujący Cię typ ogrodzenia, podmurówki czy słupków.
          </p>
        </div>

        {/* Filter pills */}
        {categories.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-6 sm:mb-8" data-testid="gallery-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`gallery-filter-${c}`}
                className={`px-4 sm:px-5 py-2 sm:py-2.5 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] border transition-all duration-200 ${
                  activeCat === c
                    ? "bg-[#ea580c] text-white border-[#ea580c]"
                    : "bg-transparent text-[#a8a29e] border-[#2a2a2e] hover:border-[#ea580c] hover:text-[#ea580c]"
                }`}
              >
                {CAT_LABELS[c] || c}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3"
          data-testid="gallery-grid"
        >
          {visible.map((it, i) => (
            <button
              key={it.id}
              type="button"
              onClick={() => setOpenIdx(i)}
              data-testid={`gallery-tile-${i}`}
              className="group relative block aspect-square overflow-hidden bg-[#131316] border border-[#2a2a2e] hover:border-[#ea580c] transition-colors"
            >
              <img
                src={resolveImageUrl(it.thumb || it.src)}
                alt={it.caption || "Galeria"}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                style={{ imageRendering: "auto", backfaceVisibility: "hidden" }}
              />
              {/* Gradient overlay (always subtle) */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b]/85 via-transparent to-transparent" />
              {/* Caption */}
              <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 text-left">
                <div className="font-display text-xs sm:text-sm uppercase text-white leading-tight tracking-tight drop-shadow line-clamp-2">
                  {it.caption}
                </div>
              </div>
              {/* Hover state */}
              <div className="absolute inset-0 bg-[#ea580c]/0 group-hover:bg-[#ea580c]/10 transition-colors duration-300" />
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 w-8 h-8 sm:w-9 sm:h-9 bg-[#ea580c] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ZoomIn className="text-white" size={16} />
              </div>
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-[#a8a29e]" data-testid="gallery-empty">
            Brak zdjęć w wybranej kategorii.
          </div>
        )}

        {/* Pokaż więcej / Zwiń */}
        {hiddenCount > 0 && !expanded && (
          <div className="mt-6 sm:mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              data-testid="gallery-expand-btn"
              className="group relative px-8 sm:px-10 py-4 sm:py-5 bg-transparent border border-[#2a2a2e] hover:border-[#ea580c] text-[#d6d3d1] hover:text-[#ea580c] font-mono text-xs uppercase tracking-[0.25em] transition-all duration-300 flex items-center gap-3"
            >
              <span>Pokaż wszystkie</span>
              <span className="text-[#ea580c] font-bold">+{hiddenCount}</span>
              <span className="inline-block group-hover:translate-y-0.5 transition-transform">↓</span>
            </button>
          </div>
        )}
        {expanded && filtered.length > INITIAL_VISIBLE && (
          <div className="mt-6 sm:mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => {
                setExpanded(false);
                setTimeout(() => {
                  const el = document.getElementById("galeria");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 60);
              }}
              data-testid="gallery-collapse-btn"
              className="px-8 sm:px-10 py-4 sm:py-5 bg-transparent border border-[#2a2a2e] hover:border-[#ea580c] text-[#a8a29e] hover:text-[#ea580c] font-mono text-xs uppercase tracking-[0.25em] transition-all duration-300 flex items-center gap-3"
            >
              <span>Zwiń galerię</span>
              <span className="inline-block">↑</span>
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {current && (
        <div
          className="fixed inset-0 z-[100] bg-[#0a0a0b]/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-cfg-in"
          onClick={close}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-11 h-11 sm:w-12 sm:h-12 bg-[#ea580c] hover:bg-[#c2410c] text-white flex items-center justify-center transition-colors"
            data-testid="gallery-close"
            aria-label="Zamknij"
          >
            <X size={22} />
          </button>

          {filtered.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-14 sm:h-14 bg-[#0a0a0b]/80 border border-[#2a2a2e] hover:border-[#ea580c] hover:text-[#ea580c] text-white flex items-center justify-center transition-colors"
                data-testid="gallery-prev"
                aria-label="Poprzednie"
              >
                <ChevronLeft size={26} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-14 sm:h-14 bg-[#0a0a0b]/80 border border-[#2a2a2e] hover:border-[#ea580c] hover:text-[#ea580c] text-white flex items-center justify-center transition-colors"
                data-testid="gallery-next"
                aria-label="Następne"
              >
                <ChevronRight size={26} />
              </button>
            </>
          )}

          <div
            className="relative max-w-6xl max-h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={resolveImageUrl(current.src)}
              alt={current.caption || "Galeria"}
              className="max-w-full max-h-[80vh] object-contain"
              data-testid="gallery-lightbox-image"
            />
            <div className="mt-4 sm:mt-6 text-center px-2">
              <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#ea580c] mb-2">
                {CAT_LABELS[current.category] || current.category}
              </div>
              <div className="font-display text-lg sm:text-2xl uppercase text-white tracking-tight">
                {current.caption}
              </div>
              {filtered.length > 1 && (
                <div className="mt-3 font-mono text-[10px] sm:text-xs text-[#57534e] uppercase tracking-widest">
                  {openIdx + 1} / {filtered.length}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
